import React from 'react';
import { Button } from 'react-bootstrap';
import { Users, PlusCircle, Sparkles, Link2, Briefcase, Unlink, Edit, Trash2, Mail, Send } from 'lucide-react';

import SettingsCard from '../SettingsCard';
import { EmptyState } from '../../Common/EmptyState';

// Users section — active users (with employee-link status), pending invitations
// and auto-suggested user↔employee mappings. Data and handlers live in the
// CompanySettings shell (lazy-loaded on first visit); role/link/invite modals
// live in the shell and are opened via the handlers passed here.
//
// Active users and pending invitations are two cards, not two `.user-section`
// blocks inside one — that nesting (card → bordered section header → bordered
// user card) was the third of the three card systems this sweep removed, and
// the only one that existed solely inside CompanySettings' inline <style>.
const UsersSection = ({
  associatedUsers,
  pendingInvitations,
  mappingSuggestions,
  getMappingForUser,
  canRemoveUser,
  handleApplySuggestion,
  handleUnlinkEmployee,
  handleOpenLinkModal,
  handleOpenRoleModal,
  handleRemoveUser,
  handleResendInvitation,
  handleDeleteInvitation,
  setShowInviteModal,
}) => (
  <>
    <SettingsCard
      id="aktiva-anvandare"
      icon={Users}
      title={`Aktiva användare (${associatedUsers.length})`}
      subtitle="Hantera teammedlemmar och deras behörigheter"
      actions={
        <Button
          variant="primary"
          className="d-flex align-items-center gap-2"
          onClick={() => setShowInviteModal(true)}
        >
          <PlusCircle size={16} />
          Bjud in användare
        </Button>
      }
    >
      {mappingSuggestions.length > 0 && (
        <div className="user-suggestions">
          <p className="user-suggestions__title">
            <Sparkles size={16} aria-hidden="true" />
            Föreslagna kopplingar ({mappingSuggestions.length})
          </p>
          <p className="user-suggestions__help">
            Dessa användare och anställda har samma e-postadress och kan kopplas
            automatiskt.
          </p>
          <ul className="settings-rows">
            {mappingSuggestions.map((suggestion, index) => (
              <li key={suggestion.user_id || index} className="settings-row">
                <div className="settings-row__text">
                  <span className="settings-row__name">
                    {suggestion.user_name} → {suggestion.employee_name}
                  </span>
                  <p className="settings-row__desc">{suggestion.user_email}</p>
                </div>
                <div className="settings-row__actions">
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => handleApplySuggestion(suggestion)}
                  >
                    <Link2 size={14} className="me-1" />
                    Koppla
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {associatedUsers.length > 0 ? (
        <ul className="settings-rows">
          {associatedUsers.map((user) => {
            const mapping = getMappingForUser(user.user_id);
            const removal = canRemoveUser(user.user_id);
            return (
              <li key={user.user_id} className="settings-row user-row">
                <div className="user-avatar" aria-hidden="true">
                  {user.first_name.charAt(0)}{user.last_name.charAt(0)}
                </div>

                <div className="settings-row__text user-row__text">
                  <span className="settings-row__name">
                    {user.first_name} {user.last_name}
                  </span>
                  <p className="user-row__contact">
                    {user.email}
                    {user.phone_number && ` · ${user.phone_number}`}
                  </p>
                  <div className="user-row__tags">
                    {user.roles.map((role) => (
                      <span key={role} className="role-badge">{role}</span>
                    ))}
                    <span className={`user-link${mapping ? ' is-linked' : ''}`}>
                      <Briefcase size={14} aria-hidden="true" />
                      {mapping
                        ? `Kopplad: ${mapping.firstname} ${mapping.lastname}`
                        : 'Ej kopplad till anställd'}
                    </span>
                  </div>
                </div>

                <div className="settings-row__actions">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => (mapping
                      ? handleUnlinkEmployee(user.user_id)
                      : handleOpenLinkModal(user))}
                    title={mapping ? 'Koppla bort anställd' : 'Koppla till anställd'}
                  >
                    {mapping ? <Unlink size={14} /> : <Link2 size={14} />}
                  </Button>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => handleOpenRoleModal(user)}
                    title="Ändra roller"
                  >
                    <Edit size={14} />
                  </Button>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => handleRemoveUser(user.user_id)}
                    disabled={!removal.canRemove}
                    title={removal.canRemove
                      ? 'Ta bort användare'
                      : 'Kan inte ta bort sista admin'}
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      ) : (
        <EmptyState
          icon={Users}
          title="Inga användare än"
          description="Bjud in teammedlemmar för att komma igång."
          actions={
            <Button variant="primary" onClick={() => setShowInviteModal(true)}>
              <PlusCircle className="me-2" size={16} />
              Bjud in användare
            </Button>
          }
        />
      )}
    </SettingsCard>

    <SettingsCard
      id="inbjudningar"
      icon={Mail}
      title={`Väntande inbjudningar (${pendingInvitations.length})`}
      subtitle="Inbjudningar som ännu inte accepterats"
      actions={pendingInvitations.length > 0 ? (
        <Button
          variant="outline-primary"
          className="d-flex align-items-center gap-2"
          onClick={() => setShowInviteModal(true)}
        >
          <PlusCircle size={16} />
          Bjud in fler
        </Button>
      ) : null}
    >
      {pendingInvitations.length > 0 ? (
        <ul className="settings-rows">
          {pendingInvitations.map((invitation) => (
            <li key={invitation.invitation_id} className="settings-row user-row">
              <div className="user-avatar user-avatar--pending" aria-hidden="true">
                <Mail size={16} />
              </div>

              <div className="settings-row__text user-row__text">
                <span className="settings-row__name">{invitation.email}</span>
                <p className="user-row__contact">
                  Inbjuden {new Date(invitation.created_at).toLocaleDateString('sv-SE')}
                  {' · '}
                  Utgår {new Date(invitation.expires_at).toLocaleDateString('sv-SE')}
                </p>
                {invitation.emailDelivery === 'failed' && <p className="text-danger">Mejlet kunde inte skickas. Använd Skicka igen.</p>}
                {invitation.emailDelivery === 'pending' && <p>Mejlleverans är ännu inte bekräftad.</p>}
                <div className="user-row__tags">
                  {invitation.roles.map((role) => (
                    <span key={role} className="role-badge pending">{role}</span>
                  ))}
                </div>
              </div>

              <div className="settings-row__actions">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => handleResendInvitation(invitation.invitation_id)}
                  title="Skicka igen"
                >
                  <Send size={14} />
                </Button>
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => handleDeleteInvitation(invitation.invitation_id)}
                  title="Ta bort inbjudan"
                >
                  <Trash2 size={14} />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <EmptyState
          icon={Mail}
          title="Inga väntande inbjudningar"
          description="Bjud in teammedlemmar för att komma igång."
          actions={
            <Button variant="primary" onClick={() => setShowInviteModal(true)}>
              <PlusCircle className="me-2" size={16} />
              Bjud in användare
            </Button>
          }
        />
      )}
    </SettingsCard>
  </>
);

export default UsersSection;
